/**
 * Dedupe concurrent triggers of the SAME async cycle into one shared run, while still
 * letting a caller that missed the whole cycle (started and finished between two of its
 * own checks) find out it happened and how it ended.
 *
 * Built for `operator-session.ts`'s rejoin cycle, which can be triggered from three
 * independent places (WS auto-reconnect's `onOpen`, `recover()`'s fallback, a manual
 * `forceRejoin`) — this replaces four loosely-coupled variables that used to track the
 * same thing by hand (`rejoinInFlight`/`rejoinPromise`/`rejoinSeq`/`lastRejoinError`).
 */
export interface SingleFlight<T> {
  /** Start `fn` if nothing is running; otherwise return the run already in flight. */
  run(fn: () => Promise<T>): Promise<T>;
  /** The in-flight run's promise, or `null` when idle. */
  readonly promise: Promise<T> | null;
  /** Monotonic count of runs started — lets a caller that raced a run to completion
   * (see `seq`'s use in `manualForceRejoin`) tell "one ran" from "none ran" after the
   * fact, without needing to have observed `promise` while it was still set. */
  readonly seq: number;
  /** Outcome of the most recently FINISHED run — `null` while one is in flight (cleared
   * at the start of every run) or after a run that succeeded. */
  readonly lastError: Error | null;
  /** Resolves after the next run to finish, whether already in flight or started fresh
   * after this call — for a caller that wants to react without polling. */
  waitForNext(): Promise<void>;
}

export function createSingleFlight<T>(): SingleFlight<T> {
  let inFlight: Promise<T> | null = null;
  let seq = 0;
  let lastError: Error | null = null;
  let waiters: Array<() => void> = [];

  function run(fn: () => Promise<T>): Promise<T> {
    if (inFlight) return inFlight;
    seq += 1;
    lastError = null;
    inFlight = (async () => {
      try {
        return await fn();
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        throw err;
      } finally {
        inFlight = null;
        const toNotify = waiters;
        waiters = [];
        toNotify.forEach((resolve) => resolve());
      }
    })();
    return inFlight;
  }

  function waitForNext(): Promise<void> {
    return new Promise((resolve) => waiters.push(resolve));
  }

  return {
    run,
    get promise() {
      return inFlight;
    },
    get seq() {
      return seq;
    },
    get lastError() {
      return lastError;
    },
    waitForNext,
  };
}
